import { initOpenApi } from "@/clients/open_api_injection";
import { FullScreenBackgroundContainer } from "@/components/container/full-screen-background-container";
import { useEnsureUserHydrated } from "@/hooks/use-ensure-user-hydrated";
import { useIsSubscriptionEnabled } from "@/hooks/use-is-subscription-enabled";
import { myFoodsQueryOptions } from "@/hooks/requests/my-foods/use-get-my-foods";
import { resolveSignedOutRoute } from "@/utils/auth/resolve-auth-destination";
import { rememberActiveSession, reportInvoluntarySignOut } from "@/utils/auth/logout-telemetry";
import { hasSignedInBefore } from "@/utils/has-signed-in-before";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { logInRevenueCat } from "@/utils/revenue-cat";
import { useAuth, useClerk, useSession } from "@clerk/clerk-react";
import { IonSpinner, useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useRef } from "react";

interface PrivateAuthGuardProps {
  children: React.ReactNode;
}

const SplashSpinner = ({ label }: { label?: string }) => (
  <FullScreenBackgroundContainer
    imageClassName="bg-splash-screen-background"
    className="flex flex-col items-center justify-center"
    backdrop={false}
  >
    <IonSpinner name="crescent" className="h-12 w-12 text-white" />
    {label ? <p className="ml-2 text-white">{label}</p> : null}
  </FullScreenBackgroundContainer>
);

export const PrivateAuthGuard = memo(function PrivateAuthGuard({
  children,
}: PrivateAuthGuardProps) {
  const { isLoaded, isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const router = useIonRouter();
  const hasRedirectedRef = useRef(false);
  const hasPrefetchedMyFoodsRef = useRef(false);
  const shouldSignOut = isLoaded && !isSignedIn;
  const { isEnabled: isSubscriptionEnabled, isResolved: isSubscriptionEnabledResolved } =
    useIsSubscriptionEnabled();

  // Self-heal a missing/corrupted USER_KEY before rendering the app, so the data
  // queries never mount against an empty USER_KEY and dead-end on "User not found".
  const { status: hydrationStatus, retry: retryHydration } = useEnsureUserHydrated(
    isLoaded && isSignedIn,
  );

  const redirectToSignedOut = useCallback(() => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    signOut()
      .catch((error) => {
        console.error("Failed to sign out", error);
      })
      .finally(() => {
        const target = resolveSignedOutRoute(hasSignedInBefore());
        if (router.routeInfo.pathname !== target) {
          router.push(target, "none", "replace");
        }
      });
  }, [router, signOut]);

  useEffect(() => {
    initOpenApi();
    if (!isLoaded) return;

    // Only sync RevenueCat once the local user is in place (post-hydration).
    if (
      !shouldSignOut &&
      hydrationStatus === "ready" &&
      isSubscriptionEnabledResolved &&
      isSubscriptionEnabled
    ) {
      try {
        const user = guardGetUserFromLocalStorage();
        void logInRevenueCat(user.id, isSubscriptionEnabled);
      } catch (error) {
        console.warn("[AuthGuard] Failed to sync RevenueCat user", error);
      }
    }

    if (shouldSignOut) {
      // Clerk reports signed-out. If this wasn't an explicit logout or a 401
      // we already recorded, capture it (e.g. iOS evicting Clerk's session).
      reportInvoluntarySignOut();
      redirectToSignedOut();
    }
  }, [
    isLoaded,
    isSubscriptionEnabled,
    isSubscriptionEnabledResolved,
    hydrationStatus,
    redirectToSignedOut,
    shouldSignOut,
  ]);

  // Remember when Clerk will end this session, so a later sign-out can be
  // classified as the normal expiry or an early (buggy) one.
  const sessionId = session?.id;
  const sessionExpireAtMs = session?.expireAt?.getTime();
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !sessionId || !sessionExpireAtMs) return;
    rememberActiveSession({ id: sessionId, expireAt: new Date(sessionExpireAtMs) });
  }, [isLoaded, isSignedIn, sessionId, sessionExpireAtMs]);

  // Hydration concluded the user can't be restored (backend has no such user, or
  // Clerk lacks id/email) — fall back to a clean signed-out state.
  useEffect(() => {
    if (hydrationStatus === "signout") {
      redirectToSignedOut();
    }
  }, [hydrationStatus, redirectToSignedOut]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      hasPrefetchedMyFoodsRef.current = false;
      return;
    }
    if (hydrationStatus !== "ready" || hasPrefetchedMyFoodsRef.current) {
      return;
    }

    hasPrefetchedMyFoodsRef.current = true;
    void queryClient.prefetchQuery(myFoodsQueryOptions);
  }, [isLoaded, isSignedIn, hydrationStatus, queryClient]);

  if (!isLoaded) {
    return <SplashSpinner label="Validating your details..." />;
  }

  if (shouldSignOut || hydrationStatus === "signout") {
    return <SplashSpinner />;
  }

  if (hydrationStatus === "hydrating") {
    return <SplashSpinner label="Restoring your session..." />;
  }

  if (hydrationStatus === "retry") {
    return (
      <FullScreenBackgroundContainer
        imageClassName="bg-splash-screen-background"
        className="flex flex-col items-center justify-center gap-4 px-6 text-center"
        backdrop={false}
      >
        <p className="text-white">We couldn't restore your session.</p>
        <button
          type="button"
          onClick={retryHydration}
          className="rounded-full bg-white px-6 py-3 font-semibold text-black"
        >
          Retry
        </button>
      </FullScreenBackgroundContainer>
    );
  }

  return children;
});
